import React, { useState, useEffect, useRef } from 'react';
import { Search, ShieldCheck, Lock, FileCheck } from 'lucide-react';
import { mockData } from '../mock';

const Benefits = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const sectionRef = useRef(null);

  const iconMap = {
    Search,
    ShieldCheck,
    Lock,
    FileCheck
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-l from-blue-500/5 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Benefícios do Pentest</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Por que escolher nossos serviços?
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Nossos testes de intrusão oferecem benefícios tangíveis para 
            fortalecer a segurança da sua organização.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {mockData.benefits.map((benefit, index) => {
            const IconComponent = iconMap[benefit.icon];
            const isHovered = hoveredBenefit === index;
            
            return (
              <div
                key={index}
                className={`group relative p-8 glass-effect border border-white/10 rounded-lg cursor-pointer transition-all duration-500 hover:border-cyan-400/50 hover:scale-105 ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
                onMouseEnter={() => setHoveredBenefit(index)}
                onMouseLeave={() => setHoveredBenefit(null)}
              >
                {/* Background Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-lg transition-opacity duration-500 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`} />

                <div className="relative z-10">
                  {/* Icon and Number */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 glass-effect border rounded-full flex items-center justify-center transition-all duration-500 ${
                      isHovered ? 'border-cyan-400 bg-cyan-400/10 scale-110' : 'border-cyan-400/30 group-hover:border-cyan-400'
                    }`}>
                      <IconComponent className={`w-8 h-8 transition-all duration-500 ${
                        isHovered ? 'text-cyan-400 scale-110' : 'text-cyan-400'
                      }`} />
                    </div>
                    
                    {/* Step Number */}
                    <div className="text-4xl font-bold text-gray-700 group-hover:text-cyan-400/30 transition-colors duration-500">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="heading-3 text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  
                  <p className="body-medium text-gray-300 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Animated Border */}
                  <div className={`h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-6 transition-all duration-500 ${
                    isHovered ? 'w-full' : 'w-0'
                  }`} />
                </div>

                {/* Floating Particles */}
                {isHovered && (
                  <div className="absolute inset-0 pointer-events-none">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
                        style={{
                          left: `${20 + Math.random() * 60}%`,
                          top: `${20 + Math.random() * 60}%`,
                          animationDelay: `${Math.random() * 2}s`,
                          animationDuration: `${1 + Math.random() * 2}s`
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '1s' }}>
          <div className="glass-effect border border-cyan-400/30 rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="heading-2 text-white mb-4">
              Proteja seu negócio com <span className="text-cyan-400">testes profissionais</span>
            </h3>
            
            <p className="body-large text-gray-300 mb-8 max-w-2xl mx-auto">
              Não espere por um incidente de segurança. Identifique vulnerabilidades 
              antes que elas sejam exploradas por atacantes.
            </p>

            {/* Statistics Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { value: '99%', label: 'Vulnerabilidades Detectadas' },
                { value: '24h', label: 'Tempo de Resposta' },
                { value: '500+', label: 'Testes Realizados' },
                { value: '100%', label: 'Confidencialidade' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-cyan-400 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>

            <button 
              onClick={() => {
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn-primary animate-glow"
            >
              Começar Avaliação de Segurança
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;